import { useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleMap,
  OverlayView,
  OverlayViewF,
  useJsApiLoader,
} from '@react-google-maps/api';
import { MarkerClusterer, SuperClusterAlgorithm, type Renderer } from '@googlemaps/markerclusterer';
import { Calendar, Gauge, Timer, Star, ChevronRight, MapPin, ChevronsUpDown } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

export interface MapPointTooltip {
  title: string;
  subtitle?: string;
  badge?: string;
  metrics?: Array<{ icon?: 'depth' | 'duration' | 'rating' | 'date'; label: string; value: string }>;
}

export interface MapPoint {
  id: number;
  lat: number;
  lng: number;
  title?: string;
  href?: string;
  tooltip?: MapPointTooltip;
}

interface MapViewProps {
  points: MapPoint[];
  height?: number | string;
  singleZoom?: number;
  rounded?: boolean;
  className?: string;
  /** When false: no zoom controls, no drag, no scroll-zoom (good for hero/teaser maps). */
  interactive?: boolean;
  /**
   * When set, the map fits to these bounds instead of all points. Useful for
   * very large point sets where fitting all points produces a useless world
   * view – instead pick a curated region the user can pan/zoom out of.
   */
  defaultBounds?: { sw: google.maps.LatLngLiteral; ne: google.maps.LatLngLiteral };
}

interface HoveredCluster {
  lat: number;
  lng: number;
  count: number;
  members: MapPoint[];
  /** When true the tooltip sits BELOW the cluster marker (cluster is near the top edge). */
  flipBelow: boolean;
}

const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) ?? '';

// Branded map style: ocean-blue water, sand-toned land, ocean-700 country lines.
// Matches the app's Tailwind palette (see styles/index.css):
//   #fbf7ef sand-50 · #f3e6cf sand-100 · #e9d4ac sand-200 · #c79c5d sand-400
//   #d2e4f3 ocean-100 · #a5c8e6 ocean-200 · #6ea4d5 ocean-300 · #3f7eb8 ocean-400
//   #1f5e98 ocean-500 · #14497b ocean-600 · #0e3a5f ocean-700 · #0a2b46 ocean-800
const MAP_STYLES: google.maps.MapTypeStyle[] = [
  // base canvas
  { elementType: 'geometry', stylers: [{ color: '#ecf3fa' }] },                                   // sand-50 = land
  { elementType: 'labels.text.fill',   stylers: [{ color: '#0a2b46' }] },                         // ocean-800
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ecf3fa' }] },                         // sand-50 (readability halo)

  // water – the brand
  { featureType: 'water', elementType: 'geometry',           stylers: [{ color: '#6ea4d5' }] },   // ocean-300
  { featureType: 'water', elementType: 'labels.text.fill',   stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#1f5e98' }] },   // ocean-500

  // landscape variations – warmer sand
  { featureType: 'landscape.natural',   elementType: 'geometry', stylers: [{ color: '#d2e4f3' }] }, // ocean-100 (slight tone-shift)
  { featureType: 'landscape.man_made',  elementType: 'geometry', stylers: [{ color: '#ecf3fa' }] }, // sand-50

  // roads – white with sand-300 hairline
  { featureType: 'road',          elementType: 'geometry',        stylers: [{ color: '#ffffff' }] },
  { featureType: 'road',          elementType: 'geometry.stroke', stylers: [{ color: '#a5c8e6' }] }, // ocean-200
  { featureType: 'road',          elementType: 'labels.text.fill', stylers: [{ color: '#0a2b46' }] }, // ocean-800
  { featureType: 'road.highway',  elementType: 'geometry',        stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'geometry',        stylers: [{ color: '#ffffff' }] },

  // POIs – muted, parks stay visible with a slight coral hint
  { featureType: 'poi',       elementType: 'labels',  stylers: [{ visibility: 'off' }] },
  { featureType: 'poi',       elementType: 'geometry', stylers: [{ color: '#d2e4f3' }] },          // ocean-100
  { featureType: 'poi.park',  elementType: 'geometry', stylers: [{ color: '#a5c8e6' }] },          // ocean-200

  // transit off – not relevant for a dive log
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },

  // administrative borders in ocean tones
  { featureType: 'administrative.country',  elementType: 'geometry.stroke', stylers: [{ color: '#3f7eb8' }, { weight: 1.2 }] }, // ocean-400
  { featureType: 'administrative.country',  elementType: 'labels.text.fill', stylers: [{ color: '#0e3a5f' }] }, // ocean-700
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#a5c8e6' }] },  // ocean-200
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#14497b' }] }, // ocean-600
];

const ICONS = {
  depth:    Gauge,
  duration: Timer,
  rating:   Star,
  date:     Calendar,
} as const;

/** Cluster bubble SVG, ocean palette, 4 size tiers. */
function clusterIcon(count: number): { url: string; size: number } {
  const tier = count < 10 ? 0 : count < 50 ? 1 : count < 200 ? 2 : 3;
  const size = [40, 48, 56, 66][tier];
  const halo = [16, 18, 20, 22][tier];
  const fontSize = [13, 14, 16, 18][tier];

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2}"        fill="#0e3a5f" fill-opacity="0.22"/>
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - halo / 2}" fill="#0e3a5f"/>
      <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle"
            fill="#ffffff" font-family="Inter, system-ui, sans-serif"
            font-size="${fontSize}" font-weight="700">${count}</text>
    </svg>`;
  return { url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(svg.trim()), size };
}

export function MapView({ points, height = 320, singleZoom = 9, rounded = true, className, interactive = true, defaultBounds }: MapViewProps) {
  const { isLoaded, loadError } = useJsApiLoader({ id: 'gmaps-script', googleMapsApiKey: apiKey });
  const [hovered, setHovered] = useState<MapPoint | null>(null);
  const [hoveredCluster, setHoveredCluster] = useState<HoveredCluster | null>(null);

  // Map must live in state, not in a ref – otherwise the cluster-building effect
  // doesn't see when onLoad finally hands us the map instance.
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const clustererRef  = useRef<MarkerClusterer | null>(null);

  const center = useMemo<google.maps.LatLngLiteral>(() => {
    if (defaultBounds) {
      return {
        lat: (defaultBounds.sw.lat + defaultBounds.ne.lat) / 2,
        lng: (defaultBounds.sw.lng + defaultBounds.ne.lng) / 2,
      };
    }
    if (points.length === 0) return { lat: 25, lng: 35 };
    if (points.length === 1) return { lat: points[0].lat, lng: points[0].lng };
    const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const lng = points.reduce((s, p) => s + p.lng, 0) / points.length;
    return { lat, lng };
  }, [points, defaultBounds]);

  useEffect(() => {
    if (!isLoaded || !map) return;

    clustererRef.current?.clearMarkers();
    clustererRef.current?.setMap(null);
    clustererRef.current = null;

    if (points.length === 0) return;

    const singleIcon = (highlight: boolean): google.maps.Symbol => ({
      path: google.maps.SymbolPath.CIRCLE,
      scale: highlight ? 9 : 7,
      fillColor: highlight ? '#f25d33' : '#0e3a5f',
      fillOpacity: 0.95,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    });

    // Maintain a Marker → MapPoint lookup so the cluster tooltip can list its members.
    const markerToPoint = new WeakMap<google.maps.Marker, MapPoint>();

    const markers = points.map((p) => {
      const marker = new google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        title: p.tooltip ? undefined : p.title,
        icon: singleIcon(false),
      });
      markerToPoint.set(marker, p);

      if (p.href) {
        marker.addListener('click', () => { window.location.href = p.href!; });
      }
      if (p.tooltip) {
        marker.addListener('mouseover', () => {
          setHovered(p);
          setHoveredCluster(null);
          marker.setIcon(singleIcon(true));
        });
        marker.addListener('mouseout', () => {
          setHovered((current) => (current?.id === p.id ? null : current));
          marker.setIcon(singleIcon(false));
        });
      }
      return marker;
    });

    const clusterRenderer: Renderer = {
      render({ count, position, markers: clusterMarkers }, _stats, m) {
        const { url, size } = clusterIcon(count);
        const clusterMarker = new google.maps.Marker({
          position,
          icon: {
            url,
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
          },
          zIndex: 1000 + count,
          map: m,
        });

        clusterMarker.addListener('mouseover', () => {
          const members = (clusterMarkers ?? [])
            .map((cm) => markerToPoint.get(cm as google.maps.Marker))
            .filter((p): p is MapPoint => !!p);

          // The cluster tooltip is ~190 px tall + ~30 px gap. Decide whether to
          // place it above or below the marker based on the map's actual pixel
          // height so we don't get clipped by the container's overflow:hidden.
          let flipBelow = false;
          const bounds   = m.getBounds();
          const mapEl    = m.getDiv();
          const mapH     = mapEl ? mapEl.clientHeight : 0;
          const TIP_H    = 220; // tooltip height + arrow
          if (bounds && mapH > 0) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const range = ne.lat() - sw.lat();
            if (range > 0) {
              const verticalRatio = (ne.lat() - position.lat()) / range; // 0 = top, 1 = bottom
              const markerPixelY  = verticalRatio * mapH;
              // Not enough room above? → flip below.
              flipBelow = markerPixelY < TIP_H + 30;
            }
          }

          setHoveredCluster({
            lat: position.lat(),
            lng: position.lng(),
            count,
            members,
            flipBelow,
          });
          setHovered(null);
        });
        clusterMarker.addListener('mouseout', () => {
          setHoveredCluster((current) => (current?.lat === position.lat() && current?.lng === position.lng() ? null : current));
        });

        return clusterMarker;
      },
    };

    // For world-scale datasets the default SuperCluster radius (60 px) collapses
    // all of Eurasia + Africa into one mega-cluster at low zoom because those
    // regions hold ~60% of our dive sites. A tighter radius gives us 5-8
    // regional clusters that match how a diver thinks about the planet.
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      renderer: clusterRenderer,
      algorithm: new SuperClusterAlgorithm({ radius: 35, maxZoom: 14, minPoints: 3 }),
    });

    if (defaultBounds) {
      const bounds = new window.google.maps.LatLngBounds(defaultBounds.sw, defaultBounds.ne);
      map.fitBounds(bounds, 20);
    } else if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, 24);
      // For a user with a single cluster of points fitBounds picks zoom 15+
      // (street level) which is useless context. Cap only the upper end so we
      // always see some surrounding coastline.
      google.maps.event.addListenerOnce(map, 'idle', () => {
        const z = map.getZoom() ?? 0;
        if (z > 12) map.setZoom(12);
      });
    }

    return () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      clustererRef.current = null;
    };
  }, [isLoaded, points, map, defaultBounds]);

  if (!apiKey) {
    return (
      <div
        className={`grid place-items-center rounded-${rounded ? 'lg' : 'none'} border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--muted-foreground)] ${className ?? ''}`}
        style={{ height }}
      >
        Karte: <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>frontend/.env.local</code> fehlt.
      </div>
    );
  }
  if (loadError) {
    return <div className="text-sm text-[var(--destructive)]">Karten konnten nicht geladen werden.</div>;
  }
  if (!isLoaded) {
    return (
      <div className={`grid place-items-center ${className ?? ''}`} style={{ height }}>
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className={`${rounded ? 'overflow-hidden rounded-xl' : ''} ${className ?? ''}`}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: typeof height === 'number' ? `${height}px` : height }}
        center={center}
        zoom={points.length === 1 ? singleZoom : 4}
        options={{
          styles: MAP_STYLES,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          // Background = same as water so blank tiles blend before they load.
          backgroundColor: '#6ea4d5',
          // Static / showcase mode locks the map down completely.
          zoomControl:              interactive,
          gestureHandling:          interactive ? 'auto' : 'none',
          disableDoubleClickZoom:  !interactive,
          scrollwheel:              interactive,
          draggable:                interactive,
          keyboardShortcuts:        interactive,
        }}
        onLoad={(m) => setMap(m)}
        onUnmount={() => setMap(null)}
      >
        {hovered?.tooltip && (
          <OverlayViewF
            position={{ lat: hovered.lat, lng: hovered.lng }}
            mapPaneName={OverlayView.FLOAT_PANE}
            getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h + 18) })}
          >
            <MapTooltipCard tooltip={hovered.tooltip} href={hovered.href} />
          </OverlayViewF>
        )}

        {hoveredCluster && (
          <OverlayViewF
            position={{ lat: hoveredCluster.lat, lng: hoveredCluster.lng }}
            mapPaneName={OverlayView.FLOAT_PANE}
            getPixelPositionOffset={(w, h) =>
              hoveredCluster.flipBelow
                ? { x: -(w / 2), y: 26 }            // tooltip BELOW the cluster
                : { x: -(w / 2), y: -(h + 26) }     // tooltip ABOVE the cluster
            }
          >
            <ClusterTooltipCard cluster={hoveredCluster} />
          </OverlayViewF>
        )}
      </GoogleMap>
    </div>
  );
}

function MapTooltipCard({ tooltip, href }: { tooltip: MapPointTooltip; href?: string }) {
  const card = (
    <div
      className="pointer-events-auto relative w-64 overflow-hidden rounded-xl bg-white text-slate-900 shadow-[0_8px_30px_rgba(14,58,95,0.18)] ring-1 ring-ocean-100"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-gradient-to-br from-ocean-700 to-ocean-500 px-4 py-3 rounded-t-xl">
        <div className="flex items-start justify-between gap-2">
          <h4 className="line-clamp-1 text-sm font-semibold text-white">{tooltip.title}</h4>
          {tooltip.badge && (
            <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-medium text-white ring-1 ring-white/30 backdrop-blur">
              {tooltip.badge}
            </span>
          )}
        </div>
        {tooltip.subtitle && (
          <p className="mt-0.5 line-clamp-1 text-xs text-ocean-100">{tooltip.subtitle}</p>
        )}
      </div>

      {tooltip.metrics && tooltip.metrics.length > 0 && (
        <div className="grid grid-cols-3 gap-2 bg-white px-4 py-3">
          {tooltip.metrics.map((m, idx) => {
            const Icon = m.icon ? ICONS[m.icon] : null;
            return (
              <div key={idx} className="text-center">
                {Icon && <Icon className="mx-auto h-3.5 w-3.5 text-ocean-700" />}
                <p className="mt-0.5 text-sm font-semibold leading-tight text-slate-900">{m.value}</p>
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{m.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {href && (
        <div className="border-t border-slate-100 bg-white px-4 py-2 text-right">
          <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-ocean-700">
            Details <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      )}

      <div
        aria-hidden
        className="absolute left-1/2 top-full -translate-x-1/2"
        style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid white' }}
      />
    </div>
  );

  return href ? (
    <a href={href} className="block cursor-pointer no-underline">{card}</a>
  ) : card;
}

function ClusterTooltipCard({ cluster }: { cluster: HoveredCluster }) {
  const visible = cluster.members.slice(0, 3);
  const hidden  = cluster.count - visible.length;

  return (
    <div
      className="pointer-events-auto relative w-64 overflow-hidden rounded-xl bg-white text-slate-900 shadow-[0_8px_30px_rgba(14,58,95,0.22)] ring-1 ring-ocean-100"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bg-gradient-to-br from-ocean-700 to-ocean-500 px-3 py-2">
        <div className="flex items-center justify-between gap-2 text-white">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" />
            <h4 className="text-xs font-semibold">{cluster.count} Tauchplätze</h4>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-white/30">
            <ChevronsUpDown className="h-2.5 w-2.5" /> Reinzoomen
          </span>
        </div>
      </div>

      <ul className="bg-white">
        {visible.map((p) => {
          const title    = p.tooltip?.title    ?? p.title ?? `Site #${p.id}`;
          const subtitle = p.tooltip?.subtitle;
          const row = (
            <div className="flex items-start gap-2 px-3 py-1.5 hover:bg-ocean-50">
              <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-ocean-700" />
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-slate-900">{title}</p>
                {subtitle && <p className="truncate text-[11px] text-slate-500">{subtitle}</p>}
              </div>
            </div>
          );
          return (
            <li key={p.id} className="border-b border-slate-100 last:border-0">
              {p.href ? <a href={p.href} className="block no-underline">{row}</a> : row}
            </li>
          );
        })}
        {hidden > 0 && (
          <li className="px-3 py-1.5 text-center text-[10px] font-medium text-slate-500">
            … und {hidden} weitere
          </li>
        )}
      </ul>

      {/* Pointer arrow – flips to the top edge of the card when the tooltip is below the marker. */}
      {cluster.flipBelow ? (
        <div
          aria-hidden
          className="absolute left-1/2 -top-2 -translate-x-1/2"
          style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid #0e3a5f' }}
        />
      ) : (
        <div
          aria-hidden
          className="absolute left-1/2 top-full -translate-x-1/2"
          style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid white' }}
        />
      )}
    </div>
  );
}
